// Section 5 Charismatic: trust changes for their household spread to two other households at
// 0.3x. Each household's change in mean trust since the season opened (SeasonReport.trustStart)
// is measured, up to the lifecycle phase, against the settlement's change; for every household
// with a Charismatic member the difference spreads to the next two households in id order
// (cyclic), scaled by the `trustChange` target with cause `charisma` (base
// config.traits.charismatic.spreadMul). Measured on the committed state (D-37).
import { clamp } from '../../core/math';
import { householdTrust } from '../../core/state-init';
import { sortedIds } from '../../core/sorted';
import type { HouseholdId, PhaseContext } from '../../core/types';
import { num } from '../shared/value';

export function spreadCharisma(ctx: PhaseContext): void {
  // Written by the report phase: read it from the committed report (Section 2 budget).
  const start = ctx.state.currentReport?.trustStart;
  if (start === undefined) return;
  const state = ctx.state;
  const ids = sortedIds(state.households).filter(
    (id) => (state.households[id]?.memberIds.length ?? 0) > 0,
  );
  const charismatic = ids.filter((id) =>
    (state.households[id]?.memberIds ?? []).some(
      (pid) => state.people[pid]?.traits.includes('charismatic') === true,
    ),
  );
  if (charismatic.length === 0) return;
  const now = householdTrust(state);
  let weighted = 0;
  let members = 0;
  for (const id of ids) {
    const before = start[id];
    const after = now[id];
    const n = state.households[id]?.memberIds.length ?? 0;
    if (before === undefined || after === undefined) continue;
    weighted += (after - before) * n;
    members += n;
  }
  if (members === 0) return;
  const settlement = weighted / members;
  const count = ctx.cfg.traits.charismatic.spreadHouseholds;
  let total = 0;
  let people = 0;
  for (const from of charismatic) {
    const before = start[from];
    const after = now[from];
    if (before === undefined || after === undefined) continue;
    const change = after - before - settlement;
    if (Math.abs(change) <= 1e-9) continue;
    const at = ids.indexOf(from);
    const targets: HouseholdId[] = [];
    for (let k = 1; k < ids.length && targets.length < count; k++) {
      const id = ids[(at + k) % ids.length];
      if (id !== undefined && id !== from) targets.push(id);
    }
    for (const hid of targets) {
      for (const pid of ctx.draft.households[hid]?.memberIds ?? []) {
        const p = ctx.draft.people[pid];
        if (p === undefined) continue;
        const delta = num(ctx, { kind: 'trustChange', personId: p.id, cause: 'charisma' }) * change;
        p.trust = clamp(p.trust + delta, 0, ctx.cfg.people.statMax);
        total += delta;
        people += 1;
      }
    }
  }
  if (people > 0)
    ctx.report.add({ kind: 'trust', cause: 'charisma', delta: total / people, people });
}
